import { io } from "socket.io-client";
import { executorSocketUrl } from "../utils/session";

// Default to this origin, where nginx / the Vite dev proxy expose the shared
// executor under /executor. Socket.io therefore lives at /executor/socket.io
// unless an explicit executor URL is configured (VITE_EXECUTOR_URL, or the
// organisation's dedicated executor), in which case it is at the root.
const EXECUTOR_URL = import.meta.env.VITE_EXECUTOR_URL || "";

class SocketService {
	constructor() {
		this.socket = null;
		this.watched = new Set();
		this.live = new Set();
	}

	connect() {
		if (!this.socket) {
			const url = executorSocketUrl() || EXECUTOR_URL;
			this.socket = io(url || window.location.origin, {
				path: url ? "/socket.io" : "/executor/socket.io",
				transports: ["websocket"],
				autoConnect: false,
			});
			// Room membership doesn't survive a reconnect: ask again
			this.socket.on("connect", () => {
				this.watched.forEach((id) => this.sendWatch(id));
				this.live.forEach((id) => this.socket.emit("live:start", { executionId: id }));
			});
		}

		if (!this.socket.connected) {
			this.socket.connect();
		}

		return this.socket;
	}

	disconnect() {
		this.watched.clear();
		this.live.clear();
		if (this.socket) {
			this.socket.disconnect();
		}
	}

	onExecutionStatus(callback) {
		if (!this.socket) this.connect();
		this.socket.on("execution:status", callback);
	}

	offExecutionStatus(callback) {
		if (this.socket) {
			this.socket.off("execution:status", callback);
		}
	}

	// A run's live updates (execution:<id>) are sent only to sockets that
	// watch it, and the executor lets one watch only after the backend confirms
	// with this user's login token that they may see the run.
	watchExecution(executionId) {
		const socket = this.connect();
		if (!this.watched.has(executionId)) {
			this.watched.add(executionId);
			if (socket.connected) this.sendWatch(executionId);
		}
		return socket;
	}

	unwatchExecution(executionId) {
		this.watched.delete(executionId);
		this.live.delete(executionId);
		if (this.socket?.connected) {
			this.socket.emit("unwatch", { executionId });
		}
	}

	// The run's browser screen, as live:<id> events ({image: base64 JPEG, url,
	// width, height}, or {ended: true}). Only for a run this socket watches:
	// the executor sends frames only to sockets the backend let watch it.
	startLiveView(executionId, onFrame) {
		const socket = this.watchExecution(executionId);
		socket.off(`live:${executionId}`);
		socket.on(`live:${executionId}`, onFrame);
		this.live.add(executionId);
		if (socket.connected) socket.emit("live:start", { executionId });
	}

	stopLiveView(executionId) {
		this.live.delete(executionId);
		if (!this.socket) return;
		this.socket.off(`live:${executionId}`);
		if (this.socket.connected) this.socket.emit("live:stop", { executionId });
	}

	sendWatch(executionId) {
		this.socket.emit("watch", {
			executionId,
			token: localStorage.getItem("token"),
		});
	}
}

export default new SocketService();
